import './AutocompleteCell.scss';
import cn from 'classnames';
import {
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useFilteredStrings } from '@src/utils/search';
import { focusCellByDelta } from './grid-focus';

interface AutocompleteCellProps {
  value: string | undefined;
  options: readonly string[];
  onCommit: (next: string) => void;
  placeholder?: string;
  cellId: string;
  disabled?: boolean;
}

const OPTION_HEIGHT = 28;
const MENU_VERTICAL_GAP = 2;
const MENU_MAX_HEIGHT = 240;

export const AutocompleteCell = ({
  value,
  options,
  onCommit,
  placeholder = 'Select',
  cellId,
  disabled = false
}: AutocompleteCellProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0
  });

  // debounceMs: 0 — grid cell autocomplete needs immediate response as users
  // type and arrow through matches; the default 150 ms felt laggy in a grid.
  const filteredOptions = useFilteredStrings(options, query, { debounceMs: 0 });

  // When the externally-committed value changes (and we aren't actively editing),
  // sync the input display.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [value, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + MENU_VERTICAL_GAP,
      left: rect.left,
      width: rect.width
    });
  }, [isOpen, filteredOptions.length]);

  // Keep highlight in view as it moves.
  useLayoutEffect(() => {
    if (!isOpen || !listRef.current) return;
    const target = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    if (target) {
      target.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const commitSelection = (selected: string): void => {
    onCommit(selected);
    setQuery('');
    setIsOpen(false);
  };

  const advance = (direction: 'next' | 'prev' | 'down' | 'up'): void => {
    if (!inputRef.current) return;
    focusCellByDelta(inputRef.current, direction);
  };

  const handleFocus = (): void => {
    setIsOpen(true);
    setQuery('');
    setHighlightedIndex(value ? Math.max(0, options.indexOf(value)) : 0);
    // Select all so typing replaces the displayed value.
    requestAnimationFrame(() => inputRef.current?.select());
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    // If focus is moving into our portaled list (e.g. mouse click), don't close.
    const next = e.relatedTarget as Node | null;
    if (next && listRef.current && listRef.current.contains(next)) return;
    setIsOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      setHighlightedIndex(i => Math.min(filteredOptions.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen && highlightedIndex > 0) {
        setHighlightedIndex(i => Math.max(0, i - 1));
        return;
      }
      // At top of list (or closed) — move to the cell above.
      setIsOpen(false);
      advance('up');
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const picked = filteredOptions[highlightedIndex];
      if (picked) {
        commitSelection(picked);
      }
      advance('next');
      return;
    }
    if (e.key === 'Tab') {
      const picked = filteredOptions[highlightedIndex];
      if (picked && isOpen) {
        onCommit(picked);
      }
      setIsOpen(false);
      setQuery('');
      // Let the browser handle native Tab focus order — don't preventDefault.
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setQuery('');
    }
  };

  const displayValue = isOpen ? query : (value ?? '');

  return (
    <>
      <input
        ref={inputRef}
        type='text'
        className='autocomplete-cell-input'
        value={displayValue}
        placeholder={disabled ? '' : placeholder}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={e => {
          setQuery(e.target.value);
          setIsOpen(true);
          setHighlightedIndex(0);
        }}
        onKeyDown={handleKeyDown}
        data-grid-cell={cellId}
        autoComplete='off'
        spellCheck={false}
      />
      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            role='listbox'
            className='autocomplete-cell-menu'
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: Math.max(menuPosition.width, 140),
              maxHeight: MENU_MAX_HEIGHT
            }}
            // Don't let mousedown on the menu blur the input.
            onMouseDown={e => e.preventDefault()}
          >
            {filteredOptions.length === 0 ? (
              <div className='autocomplete-cell-empty'>No matches</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={opt}
                  role='option'
                  aria-selected={idx === highlightedIndex}
                  tabIndex={-1}
                  className={cn('autocomplete-cell-option', {
                    active: idx === highlightedIndex
                  })}
                  style={{ height: OPTION_HEIGHT }}
                  onClick={() => {
                    commitSelection(opt);
                    advance('next');
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  {opt}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </>
  );
};
